import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { MUNICIPALITIES, PASSWORD_MIN_LENGTH, SCHOOL_FORMS } from '../../../shared/constants.ts';
import { api, type User } from '../api.ts';
import { useAuth } from '../auth.tsx';
import { Container } from '../components/Layout.tsx';
import { Alert, Button, Card, ErrorMessage, Field, PageTitle, SelectField } from '../components/ui.tsx';
import { useAction, useTitle } from '../hooks.ts';

export function AccountPage() {
  useTitle('Mitt konto');
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    firstName: user!.firstName,
    lastName: user!.lastName,
    municipality: user!.municipality,
    school: user!.school,
    schoolForm: user!.schoolForm,
    subject: user!.subject,
    emailNotifications: user!.emailNotifications,
  });
  const [saved, setSaved] = useState(false);
  const save = useAction(() => api.patch<{ user: User }>('/api/auth/me', profile));
  const set = (k: keyof typeof profile) => (e: { target: { value: string } }) => {
    setSaved(false);
    setProfile({ ...profile, [k]: e.target.value });
  };

  const [pw, setPw] = useState({ currentPassword: '', password: '' });
  const [pwDone, setPwDone] = useState(false);
  const changePw = useAction(() => api.post('/api/auth/change-password', pw));

  const [deletePw, setDeletePw] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useAction(() => api.post('/api/auth/me/delete', { password: deletePw }));

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    const res = await save.run();
    if (res) {
      setUser(res.user);
      setSaved(true);
    }
  };

  return (
    <Container>
      <PageTitle sub={user!.email}>Mitt konto</PageTitle>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-4 text-lg font-bold">Profil</h2>
          <form onSubmit={onSave} className="space-y-4">
            {saved && <Alert kind="success">Sparat.</Alert>}
            <ErrorMessage error={save.error} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Förnamn"
                value={profile.firstName}
                onChange={set('firstName')}
                error={save.error?.fields.firstName}
              />
              <Field
                label="Efternamn"
                value={profile.lastName}
                onChange={set('lastName')}
                error={save.error?.fields.lastName}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Kommun"
                value={profile.municipality}
                onChange={set('municipality')}
                options={MUNICIPALITIES}
              />
              <SelectField
                label="Skolform"
                value={profile.schoolForm}
                onChange={set('schoolForm')}
                options={SCHOOL_FORMS}
              />
            </div>
            <Field label="Skola" value={profile.school} onChange={set('school')} />
            <Field label="Ämne eller roll" value={profile.subject} onChange={set('subject')} />
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-night-800"
                checked={profile.emailNotifications}
                onChange={(e) => setProfile({ ...profile, emailNotifications: e.target.checked })}
              />
              Ta emot påminnelser och utskick från kursens lärare. (Mejl om ditt konto, rättningar och intyg
              skickas alltid.)
            </label>
            <Button type="submit" pending={save.pending}>
              Spara
            </Button>
          </form>
        </Card>

        <div className="space-y-6">
          <EmailCard />
          <Card>
            <h2 className="mb-4 text-lg font-bold">Byt lösenord</h2>
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (await changePw.run()) {
                  setPwDone(true);
                  setPw({ currentPassword: '', password: '' });
                }
              }}
            >
              {pwDone && <Alert kind="success">Lösenordet är bytt. Andra enheter har loggats ut.</Alert>}
              {changePw.error && !Object.keys(changePw.error.fields).length && (
                <ErrorMessage error={changePw.error} />
              )}
              <Field
                label="Nuvarande lösenord"
                type="password"
                autoComplete="current-password"
                value={pw.currentPassword}
                onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })}
                error={changePw.error?.fields.currentPassword}
              />
              <Field
                label="Nytt lösenord"
                type="password"
                autoComplete="new-password"
                value={pw.password}
                onChange={(e) => setPw({ ...pw, password: e.target.value })}
                hint={`Minst ${PASSWORD_MIN_LENGTH} tecken.`}
                error={changePw.error?.fields.password}
              />
              <Button type="submit" pending={changePw.pending}>
                Byt lösenord
              </Button>
            </form>
          </Card>

          <TwoFactorCard />

          <Card>
            <h2 className="text-lg font-bold">Dina uppgifter</h2>
            <p className="mt-2 text-sm text-muted">
              Ladda ned allt Aurora har sparat om dig: profil, framsteg, quizresultat, inlägg, inlämningar och
              intyg.
            </p>
            <a
              href="/api/auth/me/export"
              className="mt-3 inline-block font-semibold text-night-700 underline"
            >
              Ladda ned mina uppgifter (JSON)
            </a>

            <h3 className="mt-6 font-bold text-red-800">Ta bort kontot</h3>
            <p className="mt-1 text-sm text-muted">
              Allt om dig tas bort permanent, även framsteg och inlämningar. Intyg som redan utfärdats går
              inte längre att verifiera.
            </p>
            {confirmDelete ? (
              <form
                className="mt-3 space-y-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (await del.run()) {
                    setUser(null);
                    navigate('/');
                  }
                }}
              >
                <Field
                  label="Bekräfta med ditt lösenord"
                  type="password"
                  value={deletePw}
                  onChange={(e) => setDeletePw(e.target.value)}
                />
                <ErrorMessage error={del.error} />
                <div className="flex gap-2">
                  <Button type="submit" variant="danger" pending={del.pending}>
                    Ta bort mitt konto
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
                    Avbryt
                  </Button>
                </div>
              </form>
            ) : (
              <Button variant="secondary" className="mt-3" onClick={() => setConfirmDelete(true)}>
                Ta bort kontot …
              </Button>
            )}
          </Card>
        </div>
      </div>
    </Container>
  );
}

function EmailCard() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const change = useAction(() =>
    api.post<{ user: User; confirmationRequired: boolean }>('/api/auth/change-email', { email, password }),
  );
  const cancel = useAction(() => api.post('/api/auth/cancel-email-change'));
  if (!user) return null;
  return (
    <Card>
      <h2 className="text-lg font-bold">E-postadress</h2>
      <p className="mt-1">{user.email}</p>
      {user.pendingEmail && (
        <div className="mt-3">
          <Alert kind="info">
            Vi har skickat en länk till <strong>{user.pendingEmail}</strong>. Adressen byts när du klickar på
            länken.{' '}
            <button
              type="button"
              className="font-semibold underline"
              onClick={async () => {
                if (await cancel.run()) setUser({ ...user, pendingEmail: null });
              }}
            >
              Avbryt bytet
            </button>
          </Alert>
        </div>
      )}
      {open ? (
        <form
          className="mt-4 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            const res = await change.run();
            if (res) {
              setUser(res.user);
              setOpen(false);
              setPassword('');
            }
          }}
        >
          <Field
            label="Ny e-postadress"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={change.error?.fields.email}
          />
          {user.emailVerified && (
            <Field
              label="Ditt lösenord"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={change.error?.fields.password}
              hint="Vi skickar en länk till den nya adressen. Bytet görs när du klickar på den."
            />
          )}
          {change.error && !Object.keys(change.error.fields).length && <ErrorMessage error={change.error} />}
          <div className="flex gap-2">
            <Button type="submit" pending={change.pending}>
              Byt adress
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Avbryt
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" className="mt-3" onClick={() => setOpen(true)}>
          Byt e-postadress …
        </Button>
      )}
    </Card>
  );
}

function RecoveryCodes({ codes }: { codes: string[] }) {
  return (
    <div className="space-y-2">
      <Alert kind="success">
        Spara reservkoderna på ett säkert ställe, t.ex. i en lösenordshanterare. Varje kod fungerar en gång om
        du inte har telefonen. De visas bara nu.
      </Alert>
      <ul className="grid grid-cols-2 gap-2 rounded-xl bg-night-50 p-4 font-mono text-sm">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <button
        type="button"
        className="text-sm font-semibold text-night-700 underline"
        onClick={() => navigator.clipboard?.writeText(codes.join('\n'))}
      >
        Kopiera koderna
      </button>
    </div>
  );
}

function TwoFactorCard() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [disabling, setDisabling] = useState(false);
  const start = useAction(() => api.post<{ secret: string; qr: string }>('/api/auth/2fa/setup'));
  const enable = useAction(() => api.post<{ recoveryCodes: string[] }>('/api/auth/2fa/enable', { code }));
  const disable = useAction(() => api.post('/api/auth/2fa/disable', { password, code }));
  const regenerate = useAction(() =>
    api.post<{ recoveryCodes: string[] }>('/api/auth/2fa/recovery-codes', { code }),
  );
  const enabled = user!.twoFactorEnabled;
  useEffect(() => {
    if (window.location.hash === '#tvasteg')
      document.getElementById('tvasteg')?.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <Card>
      <h2 id="tvasteg" className="text-lg font-bold">
        Tvåstegsverifiering
      </h2>
      {user!.twoFactorRequired && !setup && !codes && (
        <Alert kind="info">
          Som lärare behöver du slå på tvåstegsverifiering för att använda lärarsidorna.
        </Alert>
      )}
      {codes ? (
        <div className="mt-3 space-y-3">
          <RecoveryCodes codes={codes} />
          <Button variant="secondary" onClick={() => setCodes(null)}>
            Jag har sparat koderna
          </Button>
        </div>
      ) : enabled ? (
        <div className="mt-2 space-y-3">
          <p className="text-sm">
            <strong className="text-green-800">Påslagen.</strong> Vid inloggning behövs en kod från appen i
            telefonen utöver lösenordet.
          </p>
          {disabling ? (
            <form
              className="space-y-3"
              onSubmit={async (e) => {
                e.preventDefault();
                if (await disable.run()) {
                  setDisabling(false);
                  setCode('');
                  setPassword('');
                  await refresh();
                }
              }}
            >
              <Field
                label="Lösenord"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={disable.error?.fields.password}
              />
              <Field
                label="Kod från appen eller en reservkod"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                error={disable.error?.fields.code}
              />
              <ErrorMessage
                error={
                  disable.error && !disable.error.fields.code && !disable.error.fields.password
                    ? disable.error
                    : null
                }
              />
              <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="danger" pending={disable.pending}>
                  Stäng av
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  pending={regenerate.pending}
                  onClick={async () => {
                    const res = await regenerate.run();
                    if (res) {
                      setDisabling(false);
                      setCode('');
                      setCodes(res.recoveryCodes);
                    }
                  }}
                >
                  Nya reservkoder
                </Button>
                <Button type="button" variant="ghost" onClick={() => setDisabling(false)}>
                  Avbryt
                </Button>
              </div>
              <ErrorMessage error={regenerate.error} />
            </form>
          ) : (
            <Button variant="secondary" onClick={() => setDisabling(true)}>
              Ändra …
            </Button>
          )}
        </div>
      ) : setup ? (
        <form
          className="mt-3 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            const res = await enable.run();
            if (res) {
              setSetup(null);
              setCode('');
              setCodes(res.recoveryCodes);
              await refresh();
            }
          }}
        >
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>Öppna en autentiseringsapp, t.ex. Microsoft Authenticator eller Google Authenticator.</li>
            <li>Lägg till ett konto och skanna QR-koden.</li>
            <li>Skriv in den sexsiffriga koden som appen visar.</li>
          </ol>
          <img
            src={setup.qr}
            alt="QR-kod för autentiseringsappen"
            className="h-48 w-48 rounded-lg ring-1 ring-night-800/10"
          />
          <p className="text-xs text-muted">
            Kan du inte skanna? Ange nyckeln manuellt: <code className="break-all">{setup.secret}</code>
          </p>
          <Field
            label="Kod från appen"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={enable.error?.fields.code}
          />
          <div className="flex gap-2">
            <Button type="submit" pending={enable.pending} disabled={code.trim().length < 6}>
              Slå på
            </Button>
            <Button type="button" variant="ghost" onClick={() => setSetup(null)}>
              Avbryt
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-2 space-y-3">
          <p className="text-sm text-muted">
            Skydda kontot med en kod från en app i telefonen utöver lösenordet.
          </p>
          <ErrorMessage error={start.error} />
          <Button
            pending={start.pending}
            onClick={async () => {
              const res = await start.run();
              if (res) setSetup(res);
            }}
          >
            Slå på tvåstegsverifiering
          </Button>
        </div>
      )}
    </Card>
  );
}
